/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { from, Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, mergeMap, switchMap } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../../models/login';
import { LoginAuthorizationService } from '../../../services/login/data-services/login-authorization/login-authorization-service';
import { LoginPostWithFingerPrintService } from '../../../services/login/data-services/login-post-with-fingerprint/login-post-with-fingerprint-service';
import { LoginSubmitDeviceService } from '../../../services/login/data-services/login-submit-device/login-submit-device.service';
import { PingOneSignalsWebSdkService } from '../../../services/login/data-services/pingone-signals-web-sdk/pingone-signals-web-sdk.service';
import { SwapAccessTokenService } from '../../../services/login/data-services/swap-access-token/swap-access-token-service';
import {
  checkRequestInputRba,
  checkRequestIsDefined,
  getResponseId,
  isCompletedStatus,
  isDeviceProfileRequiredStatus,
  isIdentifierRequiredStatus,
  returnAuthorizationResponseCode,
  returnTransformTokenDetails
} from '../../../utils/login';
import { LoginRememberMeErrorUtilService } from '../../../utils/login/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../../../utils/login/login-remember-me-utils.service';
import { PostWithCredAndDeviceFacadeService } from './post-with-cred-and-device-facade.service';

@Injectable({
  providedIn: 'root'
})
export class InitiateRbaLoginFacadeService {
  retryCount = 0;
  loginAuthorization = inject(LoginAuthorizationService);
  loginPostWithFingerPrint = inject(LoginPostWithFingerPrintService);
  loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  postWithCredAndDeviceFacade = inject(PostWithCredAndDeviceFacadeService);
  loginRememberMeErrorUtil = inject(LoginRememberMeErrorUtilService);
  swapAccessToken = inject(SwapAccessTokenService);
  pingOneSignalsWebSdkService = inject(PingOneSignalsWebSdkService);
  loginSubmitDevice = inject(LoginSubmitDeviceService);

  initiateLogin(
    loginOAuthParams: LoginOAuthParams,
    requestInput?: ILoginUserNamePasswordInput,
    appToken = false,
    eamAuthStateValue?: string,
    forcePromptLoginFlow = false
  ): Observable<AuthAccessTokenDetails | boolean | string> {
    this.postWithCredAndDeviceFacade.pkceCode = loginOAuthParams.pkceCode;
    return this.loginAuthorization
      .loginAuthorization(
        loginOAuthParams,
        eamAuthStateValue,
        forcePromptLoginFlow,
        !!loginOAuthParams.oauthConfig.enableRBA
      )
      .pipe(
        concatMap((response: LoginOAuthResponse) => {
          this.postWithCredAndDeviceFacade.flowId = getResponseId(response);
          return this.handleAuthorizationResponse(response, loginOAuthParams);
        }),
        concatMap(
          (response: LoginOAuthResponse | LoginAuthWithCredentialsResponse) => {
            return this.handleCredentialsResponse(
              response,
              loginOAuthParams,
              requestInput
            );
          }
        ),
        concatMap(
          (
            response:
              | LoginOAuthResponse
              | LoginAuthWithCredentialsResponse
              | SwapAuthForAccessTokens
              | string
              | boolean
          ) => {
            return this.processResponse(response, loginOAuthParams, appToken);
          }
        ),
        catchError((error) => {
          if (typeof error === 'string') {
            return of(error);
          } else {
            return timer(
              loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                ? loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                : 1000
            ).pipe(
              mergeMap(() => {
                this.retryCount = this.retryCount + 1;
                if (
                  this.retryCount !==
                  loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    ? loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    : 0
                ) {
                  loginOAuthParams.pkceCode =
                    this.loginRememberMeUtils.generateCodeChallenge();
                  return this.initiateLogin(loginOAuthParams, requestInput);
                } else {
                  this.retryCount = 0;
                  return of(
                    this.loginRememberMeErrorUtil.checkRememberMeErrors(
                      loginOAuthParams.oauthConfig,
                      error
                    )
                  );
                }
              })
            );
          }
        })
      );
  }

  handleAuthorizationResponse(
    response: LoginOAuthResponse,
    loginOAuthParams: LoginOAuthParams
  ): Observable<LoginAuthWithCredentialsResponse> {
    if (isIdentifierRequiredStatus(response, loginOAuthParams.oauthConfig)) {
      return of(response);
    } else if (
      isDeviceProfileRequiredStatus(response, loginOAuthParams.oauthConfig)
    ) {
      return from(this.pingOneSignalsWebSdkService.getData()).pipe(
        switchMap((deviceData: string) =>
          this.loginSubmitDevice.loginSubmitIdentifier(
            loginOAuthParams.oauthConfig,
            getResponseId(response),
            deviceData
          )
        )
      );
    } else {
      return throwError(response);
    }
  }

  handleCredentialsResponse(
    response: LoginOAuthResponse | LoginAuthWithCredentialsResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput | undefined
  ): Observable<
    | LoginOAuthResponse
    | LoginAuthWithCredentialsResponse
    | SwapAuthForAccessTokens
    | string
    | boolean
  > {
    if (
      isIdentifierRequiredStatus(
        response as LoginOAuthResponse,
        loginOAuthParams.oauthConfig
      )
    ) {
      if (requestInput) {
        return checkRequestInputRba(
          loginOAuthParams,
          response as LoginAuthWithCredentialsResponse,
          this.postWithCredAndDeviceFacade,
          checkRequestIsDefined(requestInput)
        );
      } else {
        return of(response);
      }
    } else if (
      isCompletedStatus(response as SwapAuthForAccessTokens, loginOAuthParams)
    ) {
      return this.swapAccessToken.swapAuthCodeForToken(
        loginOAuthParams,
        returnAuthorizationResponseCode(response as SwapAuthForAccessTokens)
      );
    } else {
      return throwError(response);
    }
  }

  processResponse(
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | string
      | boolean,
    loginOAuthParams: LoginOAuthParams,
    appToken = false
  ): Observable<boolean | AuthAccessTokenDetails | never> {
    if (typeof response === 'boolean') {
      return of(response);
    } else if (typeof response === 'string') {
      return throwError(response);
    } else {
      return this.processResponseHelper(response, loginOAuthParams, appToken);
    }
  }

  processResponseHelper(
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams,
    appToken = false
  ): Observable<boolean | AuthAccessTokenDetails | never> {
    if (
      isIdentifierRequiredStatus(
        response as LoginOAuthResponse,
        loginOAuthParams.oauthConfig
      )
    ) {
      this.retryCount = 0;
      return of(true);
    } else if ('access_token' in response) {
      if (!appToken) {
        this.loginRememberMeUtils.sendTokenServiceWorker(
          loginOAuthParams.oauthConfig,
          response
        );
      }
      this.retryCount = 0;
      return returnTransformTokenDetails(
        response,
        this.postWithCredAndDeviceFacade
      );
    } else {
      return throwError(response);
    }
  }
}
